import { httpsCallable } from 'firebase/functions';
import type { OpportunityPnl } from '../types';
import { functions } from './firebase';

// Keep strictly LONGER than backend timeoutSeconds (660s) so the server can
// finish or return its own error instead of a client-side deadline.
const PNL_CALLABLE_TIMEOUT_MS = 690_000;

export type GenerateOpportunityPnlResponse = {
  pnl: OpportunityPnl;
};

export function generateOpportunityPnlErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const message = String((err as { message: string }).message);
    if (message && !message.startsWith('Firebase')) {
      return message;
    }
  }
  if (err && typeof err === 'object' && 'code' in err) {
    const code = String((err as { code: string }).code);
    if (code === 'functions/invalid-argument') {
      return 'Describe your idea before generating a P&L.';
    }
    if (code === 'functions/unauthenticated') {
      return 'Sign in to generate a P&L.';
    }
    if (code === 'functions/unavailable') {
      return 'P&L service is temporarily unavailable. Try again.';
    }
    if (code === 'functions/deadline-exceeded') {
      return 'P&L generation timed out. Try again in a moment.';
    }
    if (code === 'functions/failed-precondition') {
      return 'Could not build a profitable 3-year path for this idea. Refine the wedge, pricing, or go-to-market in the Refinery tab, then regenerate.';
    }
  }
  if (err && typeof err === 'object' && 'message' in err) {
    const message = String((err as { message: string }).message);
    if (message && !message.startsWith('Firebase') && message !== 'deadline-exceeded') {
      return message;
    }
  }
  return err instanceof Error ? err.message : 'P&L generation failed.';
}

export async function generateOpportunityPnl(opportunityId: string): Promise<OpportunityPnl> {
  const fn = httpsCallable<{ opportunityId: string }, GenerateOpportunityPnlResponse>(
    functions,
    'generateOpportunityPnl',
    { timeout: PNL_CALLABLE_TIMEOUT_MS }
  );
  const result = await fn({ opportunityId: opportunityId.trim() });
  return result.data.pnl;
}
